import type { MonthKey, SummaryDataType } from "./coreTypes";

/**
 * Designates a collection of persisted monthly data buckets.
 *
 * @remarks
 * These values must remain a plain numeric enum. Budget import/export,
 * backend validators, and debug tooling use TypeScript's numeric enum reverse
 * mapping (`ItemType[ItemType.revenue] === "revenue"`) when translating API
 * payloads and CSV rows. See `docs/guides/ACCOUNTING_STANDARDS.md` for the
 * reporting methodology that depends on these item buckets.
 */
export enum ItemType {
  asset = 1,
  equity = 2,
  expense = 3,
  liability = 4,
  revenue = 5,
  customers = 6,
  customReports = 10,

  /**
   * Broad/aggregated data.
   */
  aggregatedCashflow = 7,
  /**
   * A report-owned P&L adjustment that has no chart-account identity.
   *
   * This value deliberately does not restore the legacy label aggregation.
   * It is reserved for exact, provider-documented report groups such as
   * Xero's `FXGROUPID`, which must stay separate from editable accounts while
   * still contributing to the provider's reconciled Net Profit.
   */
  aggregatedProfitAndLoss = 8,
  aggregatedBalanceSheet = 9,
}

/**
 * Optionally further classifies data for a specific item type.
 */
export enum ItemSubType {
  current = 1,
  currliab = 2,
  depreciatn = 3,
  directcosts = 4,
  equity = 5,
  expense = 6,
  fixed = 7,
  inventory = 8,
  liability = 9,
  noncurrent = 10,
  otherincome = 11,
  overheads = 12,
  prepayment = 13,
  revenue = 14,
  sales = 15,
  termliab = 16,
  paygliability = 17,
  payg = 18,
  superannuationexpense = 19,
  superannuationliability = 20,
  wagesexpense = 21,
  BankAccount = 99,
}

export enum ItemCustomClasification {
  SalesTax = 1,
  Marketing = 2,
  CustomerRevenue = 3,
}

/**
 * Generic cashflow item types that can be used for both budget and history.
 *
 * @remarks
 * Values for `ItemType.aggregatedCashflow` use cash-movement sign semantics:
 * positive values are net cash inflows and negative values are net cash
 * outflows. Known bucket codes, plus synthetic tax/payable/liability-style
 * labels, can override arithmetic sign for display because those rows are
 * conceptually cash-out buckets. The frontend separates direction from
 * magnitude for display, so this contract must stay aligned with
 * `apps/web/src/hooks/reports/utils/aggregatedCashflowUtils.ts` and
 * `docs/guides/ACCOUNTING_STANDARDS.md`.
 */
export const AGGREGATED_CASHFLOW_ITEM_TYPES = [
  "revenue",
  "expenses",
  "newBorrowing",
  "assetSales",
  "ownerContributions",
  "regularLoanRepayments",
  "lumpsumLoanRepayments",
  "assetPurchases",
  "ownerDrawings",
  "taxRefunds",
  "taxPayments",
  "other",
  "otherCashIn",
] as const;

/**
 * Bucket keys written immediately before unknown positive movements received
 * their own aggregate bucket. A persisted month with this finite denominator
 * is usable legacy history, but it is not proof that otherCashIn was zero.
 */
export const IMMEDIATE_LEGACY_AGGREGATED_CASHFLOW_ITEM_TYPES = [
  "revenue",
  "expenses",
  "newBorrowing",
  "assetSales",
  "ownerContributions",
  "regularLoanRepayments",
  "lumpsumLoanRepayments",
  "assetPurchases",
  "ownerDrawings",
  "taxRefunds",
  "taxPayments",
  "other",
] as const;

/**
 * Bucket keys written before GST refunds received their own aggregate bucket.
 * A persisted month with exactly this finite denominator is usable legacy
 * history, but it is not proof that `taxRefunds` was zero: older providers
 * could have folded that movement into another bucket.
 */
export const LEGACY_AGGREGATED_CASHFLOW_ITEM_TYPES = [
  "revenue",
  "expenses",
  "newBorrowing",
  "assetSales",
  "ownerContributions",
  "regularLoanRepayments",
  "lumpsumLoanRepayments",
  "assetPurchases",
  "ownerDrawings",
  "taxPayments",
  "other",
] as const;

export type AggregatedCashflowItemType =
  (typeof AGGREGATED_CASHFLOW_ITEM_TYPES)[number];

/**
 * Item types that are valid for organisation-specific item rows.
 */
export type OrganisationItemType =
  | ItemType.revenue
  | ItemType.expense
  | ItemType.asset
  | ItemType.liability
  | ItemType.equity;

/**
 * Item types callers may request from the public indexed-summary endpoints.
 *
 * @remarks
 * `aggregatedProfitAndLoss` is deliberately excluded: that provider-owned
 * branch is read internally by the API and projected into the ordinary P&L
 * response only after its exact report identity has been validated.
 */
export const PUBLIC_INDEXED_SUMMARY_ITEM_TYPES = [
  ItemType.revenue,
  ItemType.expense,
  ItemType.asset,
  ItemType.liability,
  ItemType.equity,
  ItemType.customers,
  ItemType.customReports,
  ItemType.aggregatedCashflow,
  ItemType.aggregatedBalanceSheet,
] as const;

export type PublicIndexedSummaryItemType =
  (typeof PUBLIC_INDEXED_SUMMARY_ITEM_TYPES)[number];

export const isPublicIndexedSummaryItemType = (
  type: number,
): type is PublicIndexedSummaryItemType =>
  PUBLIC_INDEXED_SUMMARY_ITEM_TYPES.some(
    (supportedType) => supportedType === type,
  );

export const isOrganisationItemType = (
  type: ItemType,
): type is OrganisationItemType => {
  return (
    type === ItemType.revenue ||
    type === ItemType.expense ||
    type === ItemType.asset ||
    type === ItemType.liability ||
    type === ItemType.equity
  );
};

export interface BudgetLineCategoryInput {
  fallbackCategory: string;
  itemClasification?: ItemCustomClasification | undefined;
  itemSubType?: ItemSubType | undefined;
  itemType: ItemType;
  name: string;
}

/**
 * Resolves the shared Budget Editor category used for cash-direction semantics.
 *
 * @remarks
 * Keep this package-owned because both the browser presentation and the API's
 * server-owned recommendation evidence must classify the same account exactly.
 * A category mismatch can invert a Balance Sheet recommendation's cash direction.
 */
export const resolveBudgetLineCategory = ({
  fallbackCategory,
  itemClasification,
  itemSubType,
  itemType,
  name,
}: BudgetLineCategoryInput): string => {
  const normalizedName = name.trim();

  if (normalizedName === "Sales" || /^\[Sales\b/.test(normalizedName)) {
    return "Sales";
  }

  if (
    normalizedName === "Overhead costs" ||
    /^\[Overhead costs\b/.test(normalizedName)
  ) {
    return "Overhead costs";
  }

  if (
    itemClasification === ItemCustomClasification.SalesTax ||
    itemSubType === ItemSubType.payg ||
    itemSubType === ItemSubType.paygliability
  ) {
    return "Tax payments";
  }

  if (
    itemType === ItemType.revenue &&
    (itemSubType === ItemSubType.sales || itemSubType === ItemSubType.revenue)
  ) {
    return "Sales";
  }

  if (itemType === ItemType.expense && itemSubType === ItemSubType.overheads) {
    return "Overhead costs";
  }

  if (itemType === ItemType.asset) return "Assets";
  if (itemType === ItemType.liability) return "Liabilities";

  return normalizedName || fallbackCategory;
};

export type IndexedSummaryData = {
  [dataType in SummaryDataType]?: {
    [itemType in ItemType]?: OrgData;
  };
};

/**
 * Represents organisation data indexed by `MonthKey`.
 */
export interface OrgData {
  [month: MonthKey]: MonthItems;
}

/**
 * Represents item amounts for one month, indexed by item name.
 */
export interface MonthItems {
  [itemName: string]: number;
}
